'use client';

import { reReframeVerticalsApi } from '@repo/sdk';
import { Button } from '@repo/ui';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { PipelineStepDialog } from './pipeline-step-dialog';
import { StepLogPanel } from './steps/step-log-panel';
import type { IStepActionStatus } from './use-step-action';

// Every clip is a full Snow Titan reframe round-trip; the server processes each page
// with bounded concurrency and we loop pages by cursor here, so give a generous timeout.
const REQUEST_TIMEOUT_MS = 600_000;

function appendFailures(
  append: (line: string) => void,
  failures: readonly { clipId: number; reason: string }[],
): void {
  for (const f of failures) {
    append(`    ✗ clip ${f.clipId}: ${f.reason}\n`);
  }
}

async function runReReframe(append: (line: string) => void): Promise<void> {
  let cursor: string | undefined;
  let scanned = 0;
  let reframed = 0;
  let uploaded = 0;
  let failed = 0;
  let page = 0;

  do {
    const res = await reReframeVerticalsApi(cursor ? { cursor } : undefined, {
      timeout: REQUEST_TIMEOUT_MS,
    });
    const data = res.data?.data;
    if (!data) {
      break;
    }
    scanned += data.scanned;
    reframed += data.reframed;
    uploaded += data.uploaded;
    failed += data.failed;
    page += 1;
    const remaining = data.total ?? 0;
    append(
      `Page ${page}: reframed ${data.reframed}, queued ${data.uploaded}, failed ${data.failed}  (running: reframed ${reframed}/${scanned}, failed ${failed}; ~${remaining} still need a vertical)\n`,
    );
    appendFailures(append, data.failures ?? []);
    const next = data.nextCursor;
    // Stop on a drained page or a cursor that did not advance (guards a stuck server).
    if (!next || next === cursor) {
      break;
    }
    cursor = next;
  } while (cursor);

  append(
    `\nDone. Reframed ${reframed} verticals, queued ${uploaded} to R2 (scanned ${scanned}, failed ${failed}). Snow Titan uploads them in the background.\n`,
  );
}

export function ReReframeVerticalsButton(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<IStepActionStatus>('idle');
  const [log, setLog] = useState('');

  const run = useCallback(async () => {
    setStatus('running');
    setLog('Re-reframing verticals from base masters on Snow Titan…\n');
    const append = (line: string): void => setLog((p) => p + line);
    try {
      await runReReframe(append);
      setStatus('done');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      append(`\n[error] ${message}\nProgress is saved; click again to resume.\n`);
      setStatus('error');
    }
  }, []);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Re-reframe verticals
      </Button>
      <PipelineStepDialog
        open={open}
        onOpenChange={setOpen}
        title="Re-reframe verticals"
        description="Fill in clips that have no vertical yet: derive each one from its base master on Snow Titan, then queue it to R2. Only targets clips missing a vertical (not the whole library), runs several reframes in parallel per page, and loops until drained. Idempotent and safe to re-run. Failures are usually a missing base master on disk (shown per clip)."
        primaryAction={{
          label: 'Re-reframe all',
          onClick: run,
          pending: status === 'running',
        }}
        body={<StepLogPanel status={status} log={log} />}
      />
    </>
  );
}
